import { useEffect, useRef, useState } from "react";

import { ItemListViewProps } from "../../types/ItemListViewProps.ts";
import { config } from "../../config.ts";
import Video from "../../video/jw/Video.tsx";
import { DateTime } from "luxon";
import { loadAppropriateImages } from "../../utils/image-utils.ts";
import RelativeTime from "../../date/RelativeTime.tsx";
import { VideoPlayerDetails } from "../../types/VideoPlayerDetails.ts";
import FeaturedItemHolder from "./FeaturedItemStyles.tsx";
import { ItemListItemClass } from "../../types/ItemListItemClass.ts";
import { resizeElement } from "../../utils/responsive.ts";
import LiveLabel from "./LiveLabel.tsx";
import CpacTvLabel from "./CpacTvLabel.tsx";
import GetLabel from "../../language/GetLabel.tsx";
import LiveCountdown from "../../date/LiveCountdown.tsx";
import { useStore } from "../../utils/hooks.ts";
import { Language } from "../../language/Language.ts";

const FeaturedItems = (props: ItemListViewProps) => {
  const [showCPACTV, setShowCPACTV] = useState(true);
  const lang = useStore().lang;

  // CPACWEB-277 — corruption containment for the home page featured tile.
  // A "prelive" item with no liveDateTime is corrupt (a sync-layer bug can leave the field
  // missing). Video.tsx unconditionally evaluates liveDateTime.minus(...) during render and
  // throws on null/undefined — ItemListErrorBoundary then renders an empty div in the
  // most prominent spot on the home page. Filter such items out *before* slot assignment so
  // the next valid item is promoted into the big-tile position. If filtering empties the
  // list entirely, render a graceful "Stream starting soon" placeholder rather than letting
  // the featured area collapse to a white box.
  const validItems = props.itemList.items.filter(
    (item) => !(item.type === "prelive" && !item.liveDateTime)
  );
  const incomingMainItem = validItems[0];
  const [isPlaying, setIsPlaying] = useState(false);
  const [mainItem, setMainItem] = useState<ItemListItemClass | undefined>(incomingMainItem);
  const queuedMainItem = useRef<ItemListItemClass | undefined>();
  const placeholderText =
    lang === Language.fr ? "Diffusion en direct à venir" : "Live stream starting soon";

  useEffect(() => {
    if (isPlaying) {
      queuedMainItem.current = incomingMainItem;
      return;
    }

    queuedMainItem.current = undefined;
    setMainItem(incomingMainItem);
  }, [incomingMainItem, isPlaying]);

  const handlePlaybackState = (playing: boolean) => {
    setIsPlaying(playing);
    if (!playing) {
      setMainItem(queuedMainItem.current ?? incomingMainItem);
      queuedMainItem.current = undefined;
    }
  };

  // Keep the active main item stable during playback, but let the surrounding tiles follow the
  // newest list. The queued main item is committed when JW reports pause or completion.
  const displayedItems = mainItem
    ? [
        mainItem,
        ...validItems.filter(
          (item) => item !== incomingMainItem && item !== mainItem,
        ),
      ]
    : validItems;

  useEffect(() => {
    loadAppropriateImages();
    resizeElement(".featured-container__small-item__info");
  }, [props.itemList]);

  const renderSmallItemInfo = (item: ItemListItemClass) => {
    if (!item.image) return;

    const renderTest = () => { // addition for test flag
			return (
				<div className="video-test-label t-r">
					<span>TEST</span>
				</div>
			)
		}

    switch (item.type) {
      case "article":
        if (item.image == undefined || item.title === undefined) return;
        return (
          <>
            <div className="featured-container__small-item__info__header">
              <a href={item.url} tabIndex={-1}>
                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt} itemProp="image"/>
              </a>
            </div>
            <div className="featured-container__small-item__info__description">
              <a tabIndex={-1} href={item.url} className="featured-container__small-item__info__category">
                <span itemProp="subjectOf">{item.category}</span>
              </a>
              <a href={item.url} className="featured-container__small-item__info__title">
                <span itemProp="name">{item.title}</span>
              </a>
            </div>
          </>
        );
      case "live":
        return (
          <>
            <div className="featured-container__small-item__info__header">
              <a href={item.url} rel="noreferrer" tabIndex={-1}>
                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
              </a>
            </div>
            <div className="featured-container__small-item__info__description">
              <a href={item.url} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                <span className="featured-container__small-item__info__category" itemProp="subjectOf">{item.category}</span>
                <span className="featured-container__small-item__info__title" itemProp="name">{item.title}</span>
              </a>
            </div>
            <LiveLabel />
            {item.testStream === "true" ? renderTest() : ""}
          </>
        );
        case "prelive":
        // If we are pre-live but there is no liveDateTime, skip this item - it's corrupt and we don't want to crash the page
        if (!item.liveDateTime) return null;
        const now = DateTime.now();
        const alertTime = item.liveDateTime.minus(config.liveNotificationTime);
        const eventIsSoon = now > alertTime && now < item.liveDateTime;
        const alreadyStarted = now > item.liveDateTime;

        if (alreadyStarted) {
          // The state should not be prelive here
          // but the vualto sync may not have changed
          // the state yet... so we need to show the
          // video anyway.
          return (
            <>
              <div className="featured-container__small-item__info__header">
                <a href={item.url} rel="noreferrer" tabIndex={-1}>
                  <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
                </a>
              </div>
              <div className="featured-container__small-item__info__description">
                <a href={item.url} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                  <span className="featured-container__small-item__info__category" itemProp="subjectOf">{item.category}</span>
                  <span className="featured-container__small-item__info__title" itemProp="name">{item.title}</span>
                </a>
              </div>
              <LiveLabel />
              {item.testStream === "true" ? renderTest() : ""}
            </>
          );
        }
        if (eventIsSoon) {
          // Show links and countdown
          return (
            <>
              <div className="featured-container__small-item__info__header">
                <a href={item.url} tabIndex={-1}>
                  <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
                </a>
                <div className="featured-container__small-item__live-info" aria-live="polite">
                  <GetLabel name={"LIVE"} />{" "}
                  <RelativeTime date={item.liveDateTime} />
                </div>
              </div>
              <div className="featured-container__small-item__info__description">
                <a href={item.url} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                  <span className="featured-container__small-item__info__category" itemProp="subjectOf">{item.category}</span>
                  <span className="featured-container__small-item__info__title" itemProp="name">{item.title}</span>
                </a>
              </div>
              {item.testStream === "true" ? renderTest() : ""}
            </>
          );
        }

        // Just show when it's scheduled
        return (
          <>
            <div className="featured-container__small-item__info__header">
              <a href={item.url} tabIndex={-1}>
                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
              </a>
              <div className="featured-container__small-item__live-info" aria-live="polite">
                <LiveCountdown liveDateTime={item.liveDateTime} />
              </div>
            </div>
            <div className="featured-container__small-item__info__description">
              <a href={item.url} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                  <span className="featured-container__small-item__info__category" itemProp="subjectOf">{item.category}</span>
                  <span className="featured-container__small-item__info__title" itemProp="name">{item.title}</span>
              </a>
            </div>
            {item.testStream === "true" ? renderTest() : ""}
          </>
        );
      case "cpactv":
        return (
          <>
            <div className="featured-container__small-item__info__header">
              <a href={item.url} tabIndex={-1}>
                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
              </a>
            </div>
            <div className="featured-container__small-item__info__description">
              <a tabIndex={-1} href={item.categoryURL} className="featured-container__small-item__info__category">
                <span itemProp="subjectOf">{item.category}</span>
              </a>
              <a href={item.url} className="featured-container__small-item__info__title" aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                <span itemProp="name">{item.title}</span>
              </a>
            </div>
            <CpacTvLabel showCPACTV={true} index={0} />
          </>
        );
      default:
        // Not prelive or live (or cpactv), so it's a little further in the future,
        // or it is a VOD. Don't show any special labels.
        return (
          <>
            <div className="featured-container__small-item__info__header">
              <a href={item.url} tabIndex={-1}>
                <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title} itemProp="image"/>
              </a>
            </div>
            <div className="featured-container__small-item__info__description">
              <a href={item.url} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                <span className="featured-container__small-item__info__category" itemProp="subjectOf">{item.category}</span>
                <span className="featured-container__small-item__info__title" itemProp="name">{item.title}</span>
              </a>
            </div>
            {item.testStream === "true" ? renderTest() : ""}
          </>
        );
    }
  };
  return (
    <FeaturedItemHolder>
      { props.itemList.title && <h2 id="featured_title" className="feature__title">{props.itemList.title}</h2> }
      <section className="featured-container" tabIndex={-1} itemScope itemType="https://schema.org/ItemList" aria-labelledby="featured_title">
        {displayedItems.length === 0 ? (
          <div key="placeholder" className="featured-container__big-item only-item placeholder" itemProp="itemListElement" role="status" aria-live="polite">
            <span className="featured-container__big-item__placeholder-text">
              {placeholderText}
            </span>
          </div>
        ) : (
          displayedItems.map((item, i) => {
            if (i === 0) {
              const id = `featured-container__big-item-id_${i}`;
              return (
                <div id={id} key={i} className={`featured-container__big-item ${displayedItems.length === 1 ? "only-item" : ""}`} itemProp="itemListElement">
                  {item.type === "cpactv" ? (
                    <CpacTvLabel showCPACTV={showCPACTV} index={i} />
                  ) : (
                    <></>
                  )}
                  {item.type === "live" ? (
                    <div className="featured-container__big-item__live-label-holder">
                      <LiveLabel />
                    </div>
                  ) : (
                    <></>
                  )}
                  <Video
                    id={id}
                    details={VideoPlayerDetails.createInstanceFromItemListItemClass(
                      item,
                    )}
                    setShowCPACTV={setShowCPACTV}
                    onPlaybackState={handlePlaybackState}
                    processPrelive={true}
                    hOnlyItem={displayedItems.length === 1}
                    isLanding={false}
                  />
                </div>
              );
            } else {
              return (
                <div key={i} className="featured-container__small-item" itemProp="itemListElement">
                  <div className="featured-container__small-item__info">
                    {renderSmallItemInfo(item)}
                  </div>
                </div>
              );
            }
          })
        )}
      </section>
    </FeaturedItemHolder>
  );
};
export default FeaturedItems;
